import React from "react";
import {
  InlineStack,
  Select,
  Text,
  Pagination as PolarisPagination,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";

type PaginationControlsProps = {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  pageSizeOptions?: number[];
  isLoading?: boolean;
};

const PaginationControls: React.FC<PaginationControlsProps> = ({
  page,
  pageSize,
  total,
  totalPages,
  hasNextPage,
  hasPreviousPage,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  isLoading = false,
}) => {
  const { t } = useTranslation();

  const safeTotal = Number.isFinite(total) && total > 0 ? total : 0;
  const safePageSize = Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 1;
  const safePage = Number.isFinite(page) && page > 0 ? page : 1;
  const derivedTotalPages =
    Number.isFinite(totalPages) && totalPages > 0
      ? totalPages
      : safeTotal > 0
      ? Math.ceil(safeTotal / safePageSize)
      : 1;
  const firstItemIndex =
    safeTotal === 0
      ? 0
      : Math.min((safePage - 1) * safePageSize + 1, safeTotal);
  const lastItemIndex =
    safeTotal === 0 ? 0 : Math.min(safePage * safePageSize, safeTotal);

  const summary =
    safeTotal > 0
      ? t("pagination.range_summary", {
          defaultValue: "{{from}}-{{to}} of {{total}}",
          from: firstItemIndex,
          to: lastItemIndex,
          total: safeTotal,
        })
      : t("pagination.no_items", { defaultValue: "0 items" });

  const selectOptions = pageSizeOptions.map((option) => ({
    label: option.toString(),
    value: option.toString(),
  }));

  const handleSelectChange = (value: string) => {
    const numericValue = parseInt(value, 10);
    if (!Number.isNaN(numericValue)) {
      onPageSizeChange(numericValue);
    }
  };

  const handlePrevious = () => {
    if (!isLoading && hasPreviousPage && safePage > 1) {
      onPageChange(safePage - 1);
    }
  };

  const handleNext = () => {
    if (!isLoading && hasNextPage && safePage < derivedTotalPages) {
      onPageChange(safePage + 1);
    }
  };

  return (
    <InlineStack align="space-between" blockAlign="center" gap={{ xs: "200", md: "0" }}>
      <Text as="span" variant="bodySm">
        {summary}
      </Text>
      <InlineStack gap="200" blockAlign="center">
        <Select
          label={t("pagination.items_per_page", {
            defaultValue: "Items per page",
          })}
          labelHidden
          options={selectOptions}
          value={safePageSize.toString()}
          onChange={handleSelectChange}
          disabled={isLoading}
        />
        <PolarisPagination
          hasPrevious={hasPreviousPage && safePage > 1 && !isLoading}
          onPrevious={handlePrevious}
          hasNext={hasNextPage && safePage < derivedTotalPages && !isLoading}
          onNext={handleNext}
          // Without these Polaris names the arrows in English, whatever the interface language.
          accessibilityLabels={{
            previous: t("desk_page_prev"),
            next: t("desk_page_next"),
          }}
          label={t("pagination.page_label", {
            defaultValue: "Page {{page}} of {{totalPages}}",
            page: Math.min(safePage, derivedTotalPages),
            totalPages: derivedTotalPages,
          })}
        />
      </InlineStack>
    </InlineStack>
  );
};

export default PaginationControls;
